import { useCallback, useMemo, useRef, useState } from 'react';
import { useFlag } from '@/features/unleash';
import { useSection, sections, isValidSection } from '../../useSection';
import type { CompanyReportSection } from '../../useSection';
import { ANCHOR_TO_SECTION_MAPPING } from '@/constants/anchors';
import { useReportSplitHandler } from '../ReportSplitHandlerProvider/ReportSplitHandlerProvider';
import { useIntersectionRendererContext } from '@/pages/CompanyReport/components/IntersectionRenderer';

const detailedSectionLabels = {
  summary: 'Company Overview',
  valuation: 'Valuation',
  future: 'Future Growth',
  past: 'Past Performance',
  health: 'Financial Health',
  dividend: 'Dividend',
  management: 'Management',
  ownership: 'Ownership',
  information: 'Information',
} as const;

export function useVisibleSections() {
  const flag = useFlag({ name: 'mono-loading-report' });
  const { setIntersectionDisabled } = useIntersectionRendererContext();
  const loadingFlagEnabled = flag.flagsReady && flag.enabled;

  const { onNavigation } = useReportSplitHandler();
  const targetScrollTo = useRef<CompanyReportSection | null>(null);
  const { initialSection, sectionsBelow, sectionsAbove } = useSection();
  const [visibleSections, setVisibleSections] = useState<
    Readonly<CompanyReportSection[]>
  >([initialSection]);

  const scrollToSection = useCallback(
    (section: CompanyReportSection, timeout = 600) => {
      setIntersectionDisabled(true);
      setTimeout(() => {
        const sectionEl = document.getElementById(section);
        sectionEl?.scrollIntoView();
        setIntersectionDisabled(false);
      }, timeout);
    },
    [setIntersectionDisabled]
  );

  const previousSectionLabel = useMemo(() => {
    const [firstVisibleSection] = visibleSections;
    const firstVisibleSectionIndex = sections.findIndex(
      (section) => section === firstVisibleSection
    );
    const key = sections[firstVisibleSectionIndex - 1];
    return key in detailedSectionLabels
      ? detailedSectionLabels[key]
      : 'Section';
  }, [visibleSections]);

  const nextSectionLabel = useMemo(() => {
    const lastVisibleSection = visibleSections[visibleSections.length - 1];
    const lastVisibleSectionIndex = sections.findIndex(
      (section) => section === lastVisibleSection
    );
    const key = sections[lastVisibleSectionIndex + 1];
    return key in detailedSectionLabels
      ? detailedSectionLabels[key]
      : 'Section';
  }, [visibleSections]);

  const loadNextSection = useCallback(() => {
    const [nextSection] = sectionsBelow;
    setVisibleSections((current) => {
      if (sections.includes(nextSection) && !current.includes(nextSection)) {
        return [...current, nextSection];
      }
      return current;
    });
  }, [sectionsBelow]);

  const loadEverySection = useCallback(() => {
    setVisibleSections(sections);
  }, []);

  const loadPreviousSection = useCallback(() => {
    const previousSection = sectionsAbove[sectionsAbove.length - 1];
    if (typeof previousSection !== 'undefined') {
      setVisibleSections((current) => {
        if (
          sections.includes(previousSection) &&
          !current.includes(previousSection)
        ) {
          return [previousSection, ...current];
        }
        return current;
      });
    }
  }, [sectionsAbove]);

  const loadSection = useCallback(
    (
      rawTargetSection:
        | CompanyReportSection
        | keyof typeof ANCHOR_TO_SECTION_MAPPING
    ) => {
      let targetSection: CompanyReportSection = 'summary';
      if (isValidSection(rawTargetSection)) {
        targetSection = rawTargetSection;
      } else {
        targetSection = ANCHOR_TO_SECTION_MAPPING[rawTargetSection];
      }

      /**
       *
       * Ensure we only go through this process
       * if `targetSection` is not part of the `visibleSection` list
       *
       * */
      if (!visibleSections.includes(targetSection)) {
        targetScrollTo.current = targetSection;
        const targetSectionIndex = sections.findIndex(
          (sourceSection) => sourceSection === targetSection
        );

        const lastVisibleSection = visibleSections[visibleSections.length - 1];
        const lastVisibleSectionIndex = sections.findIndex(
          (sourceSection) => sourceSection === lastVisibleSection
        );

        const newSections: CompanyReportSection[] = [];

        /**
         * To maintain sorting order of `sections` object we'll iterate
         * over `sections` list and "pick" what are needed to be visible
         * into `newSections` array
         * */
        sections.forEach((section, index) => {
          /**
           * Append currently visible sections
           * These are what's already within `visibleSection` state
           * */
          if (visibleSections.includes(section)) {
            newSections.push(section);
            return;
          }

          /**
           * Append sections between target and first visible section
           *
           * eg
           * sections = ['summary', 'valuation', 'past']
           * visibleSections state = ['past']
           * targetSection = 'summary'
           *
           * We'll be appending 'summary' and 'valuation' into the `newSections` list
           * according to this condition
           * */
          if (
            index >= targetSectionIndex &&
            index < lastVisibleSectionIndex &&
            targetSectionIndex < lastVisibleSectionIndex
          ) {
            newSections.push(section);
            return;
          }

          /**
           * Append sections between last visible section and target section
           *
           * eg
           * sections = ['summary', 'valuation', 'past', 'future']
           * visibleSections state = ['valuation']
           * targetSection = 'future'
           *
           * We'll be appending 'past' and 'future' into the `newSections` list
           * according to this condition
           * */
          if (index > lastVisibleSectionIndex && index <= targetSectionIndex) {
            newSections.push(section);
            return;
          }
        });

        setVisibleSections(newSections);
      } else {
        scrollToSection(targetSection, 0);
      }
      onNavigation(targetSection, 'replace');
    },
    [visibleSections, onNavigation, scrollToSection]
  );

  const handleSectionOnMount = useCallback(
    (section: CompanyReportSection) => {
      if (targetScrollTo.current === section) {
        const waitingTime = loadingFlagEnabled ? 0 : 600;
        scrollToSection(targetScrollTo.current, waitingTime);
        targetScrollTo.current = null;
      }
    },
    [loadingFlagEnabled, scrollToSection]
  );

  const hasNextSection = sectionsBelow.length > 0;
  const hasPreviousSection = !visibleSections.includes('summary');

  /** Implies there's only one section visible */
  const isPristine = useMemo(
    () => visibleSections.length === 1,
    [visibleSections]
  );

  const getSectionLabel = useCallback((section: CompanyReportSection) => {
    return detailedSectionLabels[section];
  }, []);

  return {
    isPristine,
    visibleSections,
    previousSectionLabel,
    nextSectionLabel,
    loadNextSection,
    loadPreviousSection,
    loadEverySection,
    hasNextSection,
    hasPreviousSection,
    loadSection,
    handleSectionOnMount,
    scrollToSection,
    getSectionLabel,
  };
}
